import { useEffect, useState, useCallback } from 'react'
import { api } from '../api/axios'
import {
  getSyncQueue,
  removeFromSyncQueue,
  updateSyncQueueError,
  getPendingSyncCount,
  MAX_SYNC_RETRIES,
  type SyncQueueItem,
} from '../lib/offlineDb'
import { useToast } from '../components/offline/ToastProvider'

// `useOfflineSync()` roda em varios lugares ao mesmo tempo (SyncStatusBar fica
// montado em toda pagina autenticada, e OfflineQueuePage tambem usa o hook) —
// cada instancia tem seu proprio estado `syncing`, entao esse guard local nao
// impede duas instancias de chamarem syncPending() juntas quando a internet
// volta, o que reenviaria os mesmos itens da fila duas vezes. Este lock e' em
// escopo de modulo (compartilhado por todas as instancias do hook) para
// garantir que só uma sincronizacao real rode por vez.
let syncEmAndamento: Promise<SyncResult> | null = null

export function useNetworkStatus() {
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine)

  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  return isOnline
}

export interface SyncResult {
  success: number
  failed: number
  errors: string[]
}

export function useOfflineSync() {
  const isOnline = useNetworkStatus()
  const [syncing, setSyncing] = useState(false)
  const [pendingCount, setPendingCount] = useState(0)
  const [lastSync, setLastSync] = useState<Date | null>(null)
  const { addToast } = useToast()

  const refreshPendingCount = useCallback(async () => {
    const count = await getPendingSyncCount()
    setPendingCount(count)
  }, [])

  useEffect(() => {
    refreshPendingCount()
    const interval = setInterval(refreshPendingCount, 5000)
    return () => clearInterval(interval)
  }, [refreshPendingCount])

  // Notifica offline/online
  useEffect(() => {
    if (!isOnline) {
      addToast('Modo offline ativado', 'warning')
    } else {
      addToast('Conexao restabelecida', 'success')
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline])

  // Sincroniza automaticamente quando volta online
  useEffect(() => {
    if (isOnline && pendingCount > 0) {
      syncPending()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline])

  async function syncPending(): Promise<SyncResult> {
    // Se outra instancia do hook (ex: SyncStatusBar) ja esta sincronizando,
    // espera o mesmo resultado em vez de reenviar a fila em paralelo.
    if (syncEmAndamento) {
      setSyncing(true)
      const resultado = await syncEmAndamento
      setSyncing(false)
      await refreshPendingCount()
      return resultado
    }

    const execucao = (async (): Promise<SyncResult> => {
      const queue = await getSyncQueue()
      let success = 0
      let failed = 0
      const errors: string[] = []

      for (const item of queue) {
        try {
          if (item.retryCount >= MAX_SYNC_RETRIES) {
            failed++
            errors.push(`${item.table} #${item.id}: max retries`)
            continue
          }
          await api.request({
            url: item.endpoint,
            method: item.method,
            data: item.payload,
          })
          await removeFromSyncQueue(item.id!)
          success++
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : 'Erro desconhecido'
          await updateSyncQueueError(item.id!, msg)
          failed++
          errors.push(`${item.table} #${item.id}: ${msg}`)
        }
      }

      if (success > 0) {
        addToast(`${success} item(s) sincronizado(s)`, 'success')
      }
      if (failed > 0) {
        addToast(`${failed} falha(s) na sincronizacao`, 'error')
      }
      if (success === 0 && failed === 0 && queue.length === 0) {
        addToast('Nada para sincronizar', 'info')
      }

      return { success, failed, errors }
    })()

    syncEmAndamento = execucao
    setSyncing(true)
    try {
      const resultado = await execucao
      setLastSync(new Date())
      return resultado
    } finally {
      syncEmAndamento = null
      setSyncing(false)
      await refreshPendingCount()
    }
  }

  return {
    isOnline,
    syncing,
    pendingCount,
    lastSync,
    syncPending,
    refreshPendingCount,
  }
}
